import React, { useEffect, useState } from 'react';
import { FaCog, FaSave } from 'react-icons/fa';
import api from '../services/api';
import { useFeatures } from '../services/features';
import { NUMBER_FORMAT_HINT, previewNumberFormat } from '../services/invoiceNumbering';
import { BALANCING_MODEL_LABELS, describeApiError } from '../services/balancing';
import { INVOICE_GROUPING_OPTIONS, settingsChanges, settingsFormFor } from '../services/instanceSettings';

// Dwa kierunki fakturowania, każdy z własnym wariantem dla JST i jednostek
const GROUPING_FIELDS = [
    {
        field: 'groupingUserPaysToCoop',
        label: 'Faktury spółdzielni dla członków',
        hint: 'Faktury za energię pobraną i faktury za bilansowanie.',
    },
    {
        field: 'groupingCoopPaysToUser',
        label: 'Faktury członków dla spółdzielni',
        hint: 'Faktury i dowody zakupu za energię oddaną.',
    },
];

const Settings = () => {
    const features = useFeatures();
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await api.get('/settings/');
                setSettings(response.data);
                setForm(settingsFormFor(response.data));
            } catch (error) {
                console.error('Failed to fetch settings', error);
                setError(describeApiError(error, 'Nie udało się pobrać ustawień'));
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const updateField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const { changes, errors } = settings && form ? settingsChanges(settings, form) : { changes: {}, errors: {} };
    const dirty = Object.keys(changes).length > 0;
    const invalid = Object.keys(errors).length > 0;

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMessage('');
        if (invalid) {
            setError(Object.values(errors)[0]);
            return;
        }
        if (!dirty) return;

        try {
            setSaving(true);
            const response = await api.put('/settings/', changes);
            setSettings(response.data);
            setForm(settingsFormFor(response.data));
            setSuccessMessage('Zapisano ustawienia — obowiązują od następnego rozliczenia');
        } catch (error) {
            console.error('Failed to update settings', error);
            setError(describeApiError(error, 'Nie udało się zapisać ustawień'));
        } finally {
            setSaving(false);
        }
    };

    const fixedRate = settings?.balancing_model === 'fixed_rate';
    const selectedOption = (field) => INVOICE_GROUPING_OPTIONS.find((option) => option.value === form[field]);
    const formatText = form ? form.numberFormat.trim() : '';

    const renderBody = () => {
        if (loading) {
            return (
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <p className="text-center py-4">Ładowanie...</p>
                </div>
            );
        }

        // Bez ustawień (np. konto tylko do odczytu dostaje 403) nie ma czego pokazać — błąd jest wyżej
        if (!settings || !form) return null;

        // Oba działy dotyczą fakturowania; bez modułu nie ma co zmieniać
        if (features && !features.invoicing) {
            return (
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <p className="text-gray-600">
                        Fakturowanie jest wyłączone w tej instancji — ustawienia fakturowania nie mają zastosowania. Włącza je operator.
                    </p>
                </div>
            );
        }

        return (
            <form onSubmit={handleSave}>
                {/* Warianty fakturowania gminy i jednostek */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
                    <h3 className="text-lg font-bold mb-1">Faktury JST i jednostek</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        Podatnikiem VAT jest gmina, a jej jednostki (szkoła, zakład komunalny) mają własne PPE. Wariant decyduje,
                        ile faktur dostaje gmina i czy jednostka jest na fakturze. Każde PPE ma zawsze własny wiersz i własne saldo.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {GROUPING_FIELDS.map(({ field, label, hint }) => (
                            <div key={field}>
                                <label className="block text-gray-700 mb-2">{label}:</label>
                                <select
                                    value={form[field]}
                                    onChange={updateField(field)}
                                    className="w-full p-2 border border-gray-300 rounded-lg"
                                >
                                    {INVOICE_GROUPING_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <p className="text-gray-500 text-xs mt-1">
                                    {hint} {selectedOption(field)?.description}
                                </p>
                            </div>
                        ))}
                    </div>
                    <p className="text-gray-500 text-xs">
                        Zmiana wariantu działa od następnego rozliczenia, wystawione faktury zostają bez zmian. Żeby wystawić miesiąc
                        jeszcze raz w innym wariancie, usuń rozliczenie tego miesiąca (Generowanie faktur) — tylko gdy żadna z jego
                        faktur nie trafiła jeszcze do KSeF. Dla członka bez jednostek warianty „Jedna na JST” i „Jedna na jednostkę”
                        dają to samo.
                    </p>
                </div>

                {/* Bilansowanie: stawka i numeracja; włącznik i model ustawia operator */}
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
                    <h3 className="text-lg font-bold mb-1">Bilansowanie</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        Stan: <span className="font-semibold">{settings.balancing_active ? 'włączone' : 'wyłączone'}</span>,
                        model: <span className="font-semibold">{BALANCING_MODEL_LABELS[settings.balancing_model] || settings.balancing_model}</span>
                        {' '}(ustawia operator). Faktury za bilansowanie idą według wariantu „Faktury spółdzielni dla członków”.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {fixedRate && (
                            <div>
                                <label className="block text-gray-700 mb-2">Stawka za bilansowanie (zł netto/MWh):</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={form.rate}
                                    onChange={updateField('rate')}
                                    className={`w-full p-2 border rounded-lg ${errors.rate ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="np. 25,00"
                                />
                                {errors.rate ? (
                                    <p className="text-red-500 text-sm mt-1">{errors.rate}</p>
                                ) : (
                                    <p className="text-gray-500 text-xs mt-1">
                                        Każda zbilansowana MWh kosztuje tę samą stawkę. Bez stawki faktury za bilansowanie nie powstaną.
                                    </p>
                                )}
                            </div>
                        )}
                        <div>
                            <label className="block text-gray-700 mb-2">Format numeru faktury za bilansowanie:</label>
                            <input
                                type="text"
                                value={form.numberFormat}
                                onChange={updateField('numberFormat')}
                                className={`w-full p-2 border rounded-lg font-mono ${errors.numberFormat ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="{nn}/{mm}/BILANSOWANIE ENERGII/{yyyy}"
                            />
                            {errors.numberFormat ? (
                                <p className="text-red-500 text-sm mt-1">{errors.numberFormat}</p>
                            ) : (
                                <p className="text-gray-600 text-sm mt-1">
                                    Przykład: <span className="font-mono">{previewNumberFormat(formatText)}</span>
                                </p>
                            )}
                            <p className="text-gray-500 text-xs mt-1">
                                {NUMBER_FORMAT_HINT} Faktury za bilansowanie mają własną serię, osobną od faktur za energię.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={saving || !dirty || invalid}
                        className="bg-softTeal text-white font-semibold px-6 py-2 rounded-lg hover:bg-warmOrange transition disabled:opacity-50 flex items-center"
                    >
                        <FaSave className="mr-2" />
                        {saving ? 'Zapisywanie...' : 'Zapisz ustawienia'}
                    </button>
                </div>
            </form>
        );
    };

    return (
        <div className="min-h-screen p-4">
            <main className="container mx-auto">
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg mb-6">
                    <h2 className="text-xl sm:text-2xl font-bold flex items-center mb-2">
                        <FaCog className="mr-2 text-softTeal" />
                        Ustawienia spółdzielni
                    </h2>
                    <p className="text-gray-600 text-sm">
                        Parametry fakturowania, które zmienia administrator spółdzielni. Pozostałe ustawienia instancji — fakturowanie,
                        KSeF, włącznik i model bilansowania — ustawia operator na stronie instancji.
                    </p>
                </div>

                {successMessage && (
                    <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                        {successMessage}
                    </div>
                )}
                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}

                {renderBody()}
            </main>
        </div>
    );
};

export default Settings;
